/**
 * 脚本沙箱的主线程编排（M4-09）。
 *
 * 一次只跑一条脚本；`run()` 建一个新 Worker、转发它的 API 调用到
 * `api.ts` 的实现、把 `log`/`done`/`error` 事件交给调用方。Worker 工厂可注入
 * ——同 `io/codecs/index.ts` 的 codec worker，测试用假 Worker 驱动，不必真起线程。
 */
import type { AppStore } from '../store';
import { dispatchScriptCommand, snapshotDocument, snapshotPixels, snapshotSelection } from './api';
import type { RecordedStep } from '../actions/action-recorder';
import type { Rect } from '../../document/types';

interface CallMessage {
  readonly type: 'call';
  readonly id: number;
  readonly method: string;
  readonly params: unknown;
}
interface LogMessage {
  readonly type: 'log';
  readonly args: readonly string[];
}
interface DoneMessage {
  readonly type: 'done';
}
interface ErrorMessage {
  readonly type: 'error';
  readonly message: string;
}
type WorkerMessage = CallMessage | LogMessage | DoneMessage | ErrorMessage;

export interface ScriptRunResult {
  readonly ok: boolean;
  readonly error?: string;
}

export interface ScriptSandboxDeps {
  readonly store: AppStore;
  /** Worker 工厂，注入以便测试。缺省用真实的 `sandbox-worker.ts`。 */
  readonly workerFactory?: () => Worker;
}

function defaultWorkerFactory(): Worker {
  return new Worker(new URL('./sandbox-worker.ts', import.meta.url), { type: 'module' });
}

export class ScriptSandbox {
  readonly #deps: ScriptSandboxDeps;
  #worker: Worker | null = null;

  constructor(deps: ScriptSandboxDeps) {
    this.#deps = deps;
  }

  get running(): boolean {
    return this.#worker !== null;
  }

  /** 中止当前正在跑的脚本（若有）。不算错误——用户主动打断是正常路径。 */
  stop(): void {
    this.#worker?.terminate();
    this.#worker = null;
  }

  run(code: string, onLog: (line: string) => void): Promise<ScriptRunResult> {
    this.stop();
    const worker = (this.#deps.workerFactory ?? defaultWorkerFactory)();
    this.#worker = worker;

    return new Promise<ScriptRunResult>((resolve) => {
      const finish = (result: ScriptRunResult): void => {
        worker.terminate();
        if (this.#worker === worker) this.#worker = null;
        resolve(result);
      };

      worker.onmessage = (event: MessageEvent<WorkerMessage>): void => {
        const msg = event.data;
        if (msg.type === 'log') {
          onLog(msg.args.join(' '));
          return;
        }
        if (msg.type === 'call') {
          void this.#handleCall(worker, msg.id, msg.method, msg.params);
          return;
        }
        if (msg.type === 'done') {
          finish({ ok: true });
          return;
        }
        if (msg.type === 'error') {
          finish({ ok: false, error: msg.message });
          return;
        }
      };
      worker.onerror = (event: ErrorEvent): void => {
        finish({ ok: false, error: event.message });
      };

      worker.postMessage({ type: 'run', code });
    });
  }

  async #handleCall(worker: Worker, id: number, method: string, params: unknown): Promise<void> {
    try {
      const value = await this.#invoke(method, params);
      worker.postMessage({ type: 'result', id, ok: true, value });
    } catch (error) {
      worker.postMessage({
        type: 'result',
        id,
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  async #invoke(method: string, params: unknown): Promise<unknown> {
    const store = this.#deps.store;
    switch (method) {
      case 'getDocument':
        return snapshotDocument(store);
      case 'getSelection':
        return snapshotSelection(store);
      case 'getPixels':
        return snapshotPixels(store, params as Rect);
      case 'dispatch':
        dispatchScriptCommand(store, params as RecordedStep);
        return null;
      default:
        throw new Error(`未知的脚本 API 方法：${method}`);
    }
  }
}
